<template>
  <transition name="v2026-modal-fade">
    <div v-if="open" class="v2026-modal-backdrop" :style="backdropAlign" @mousedown="onBackdropDown">
      <div class="v2026-modal-dialog" :style="dialogStyle">
        <slot/>
      </div>
    </div>
  </transition>
</template>

<script>
const V_ALIGN = { top: 'flex-start', center: 'center', bottom: 'flex-end' }
const H_ALIGN = { left: 'flex-start', center: 'center', right: 'flex-end' }

export default {
  name: 'BaseModal',
  props: {
    // Also drives the ESC listener and the body scroll lock.
    open: {
      type: Boolean,
      default: false
    },
    closeOnBackdrop: {
      type: Boolean,
      default: true
    },
    maxWidth: {
      type: String,
      default: '560px'
    },
    // [vertical, horizontal] — top|center|bottom, left|center|right.
    position: {
      type: Array,
      default: () => ['center', 'center'],
      validator: (v) => Array.isArray(v) && v.length === 2
    },
    // Extra dialog CSS as "prop:value" strings, e.g. ['margin-top:10%', 'padding:0'].
    modalStyle: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    backdropAlign () {
      const vertical = (this.position && this.position[0]) || 'center'
      const horizontal = (this.position && this.position[1]) || 'center'
      return {
        alignItems: V_ALIGN[vertical] || 'center',
        justifyContent: H_ALIGN[horizontal] || 'center'
      }
    },
    // Split on the FIRST colon so values containing ':' survive; malformed entries are skipped.
    dialogStyle () {
      const style = { maxWidth: this.maxWidth }
      for (const decl of (this.modalStyle || [])) {
        if (typeof decl !== 'string') continue
        const i = decl.indexOf(':')
        if (i === -1) continue
        const prop = decl.slice(0, i).trim()
        const val = decl.slice(i + 1).trim()
        if (prop) style[prop] = val
      }
      return style
    }
  },
  watch: {
    open (isOpen) {
      this.syncOpenState(isOpen)
    }
  },
  mounted () {
    this.syncOpenState(this.open)
  },
  beforeDestroy () {
    document.removeEventListener('keydown', this.onKeydown)
    this.unlockBody()
  },
  methods: {
    syncOpenState (isOpen) {
      if (isOpen) {
        document.addEventListener('keydown', this.onKeydown)
        this.lockBody()
      } else {
        document.removeEventListener('keydown', this.onKeydown)
        this.unlockBody()
      }
    },
    // Only when the mousedown lands on the backdrop itself; clicks inside the dialog bubble up
    // with a different target and must not close.
    onBackdropDown (e) {
      if (!this.closeOnBackdrop) return
      if (e.target !== e.currentTarget) return
      this.$emit('close')
    },
    onKeydown (e) {
      if (e.key === 'Escape') this.$emit('close')
    },
    lockBody () {
      document.body.style.overflow = 'hidden'
    },
    unlockBody () {
      document.body.style.overflow = ''
    }
  }
}
</script>

<style scoped>
.v2026-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1060;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.45);
  padding: 16px;
}
.v2026-modal-dialog {
  width: 100%;
  max-height: 90vh;
  overflow: hidden;
  background: #F8F9FA;
  border-radius: 24px;
  box-shadow: 0px 8px 28px 0px #00000033;
}
.v2026-modal-fade-enter-active,
.v2026-modal-fade-leave-active {
  transition: opacity 0.15s ease;
}
.v2026-modal-fade-enter,
.v2026-modal-fade-leave-to {
  opacity: 0;
}
</style>
